import { useCallback, type KeyboardEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip, mergeClasses } from '@fluentui/react-components';
import type { RailItem, RailState } from '@/types/leftNavRail.types';
import { useNavRailItemStyles } from './NavRailItem.styles';

export interface NavRailItemProps {
  readonly item: RailItem;
  readonly isActive: boolean;
  readonly railState: RailState;
  readonly onSelect: (item: RailItem, event: MouseEvent | KeyboardEvent) => void;
  /** When true, suppress the collapsed-state Tooltip (parent rows use a Popover flyout instead). */
  readonly disableCollapsedTooltip?: boolean;
  /** Optional extra key handler, e.g. for collapsed-rail flyout arrow navigation. */
  readonly onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void;
}

export const NavRailItem = ({
  item,
  isActive,
  railState,
  onSelect,
  disableCollapsedTooltip = false,
  onKeyDown,
}: NavRailItemProps) => {
  const styles = useNavRailItemStyles();
  const { t } = useTranslation();
  const Icon = isActive ? item.iconActive : item.icon;
  const label = t(item.labelKey);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onSelect(item, event);
    },
    [item, onSelect]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        onSelect(item, event);
      }
      onKeyDown?.(event);
    },
    [item, onSelect, onKeyDown]
  );

  const link = (
    <Link
      to={item.defaultRoute}
      className={mergeClasses(styles.root, isActive && styles.rootActive)}
      data-testid={item.testId}
      data-rail-item-key={item.key}
      aria-current={isActive ? 'page' : undefined}
      aria-label={railState === 'collapsed' ? label : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <span className={styles.iconColumn} aria-hidden="true">
        <Icon />
      </span>
      <span className={mergeClasses(styles.label, railState === 'collapsed' && styles.labelHidden)}>
        {label}
      </span>
    </Link>
  );

  if (railState === 'collapsed' && !disableCollapsedTooltip) {
    return (
      <Tooltip content={label} relationship="label" positioning="after" withArrow>
        {link}
      </Tooltip>
    );
  }
  return link;
};

NavRailItem.displayName = 'NavRailItem';
